import { useCreateWizardMutation, useSaveWizardDraftMutation } from '../api/wizardsApi';
import type { WizardConfig, WizardStepData } from '../types/wizard';

/**
 * useWizardSubmit Hook
 *
 * Handles wizard submission (draft or complete).
 * Based on wizardForm.js crunch() and wizardFormWrapper.js onSubmit()
 *
 * Features:
 * - submitDraft: Save partial progress
 * - submitComplete: Create final wizard with recipe generation
 */
export const useWizardSubmit = (
  appId: string,
  wizardConfig: WizardConfig | null,
  wizardData: WizardStepData
) => {
  const [createWizard, { isLoading: isCreating }] = useCreateWizardMutation();
  const [saveDraft, { isLoading: isSaving }] = useSaveWizardDraftMutation();

  const submitDraft = async (currentStep: number, wizardId?: string) => {
    if (!wizardConfig) throw new Error('Wizard configuration is missing');

    const payload = {
      appId,
      wizardId,
      name: wizardData.name!,
      sourceType: wizardConfig.source.type,
      destinationType: wizardConfig.destination.type,
      partialData: { ...wizardData, currentStep },
      currentStep,
    };

    return await saveDraft(payload).unwrap();
  };

  const submitComplete = async () => {
    if (!wizardConfig) throw new Error('Wizard configuration is missing');

    // Transform wizard data to final config
    const finalConfig: WizardConfig = {
      title: wizardData.name!,
      typeGuid: wizardConfig.typeGuid,
      typeId: wizardConfig.typeId,
      source: {
        ...wizardConfig.source,
        environmentId: wizardData.sourceEnv,
        connectionKey: wizardData.sourceConnection,
      },
      destination: {
        ...wizardConfig.destination,
        environmentId: wizardData.destinationEnv,
        connectionKey: wizardData.destinationConnection,
      },
      mappings: wizardData.mappings,
      schedule: wizardData.schedule,
    };

    const payload = {
      appId,
      name: wizardData.name!,
      sourceType: wizardConfig.source.type,
      destinationType: wizardConfig.destination.type,
      config: finalConfig,
    };

    return await createWizard(payload).unwrap();
  };

  return {
    submitDraft,
    submitComplete,
    isCreating,
    isSaving,
  };
};
